import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  HostListener,
  ChangeDetectionStrategy,
  signal,
  computed,
  inject,
  Optional,
  OnChanges,
  SimpleChanges,
  input
} from '@angular/core';
import { BreakpointService } from '../../../core/services/breakpoint.service';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { CommonModule } from '@angular/common';
import { CommonService } from '../../../core/services/common.service';
import { AuditService } from '../../../core/services/audit.service';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import {
  DropdownComponent,
  type DropdownOption,
} from '../ui/dropdown/dropdown.component';
import { IconComponentV2 } from '../icon-component/icon.component';
import { type IconName } from '../../icons/icon.component';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';
import { getCalendarEquivalentPreviousPeriod } from '../../utils/date-utils';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);

// Set default timezone to Los Angeles
const LA_TIMEZONE = 'America/Los_Angeles';

// Helper function to get current time in LA timezone
const nowInLA = () => dayjs().tz(LA_TIMEZONE);

/** Parse UTC ISO strings from the API/service into LA wall time (do not use dayjs.tz(iso, LA) on Z-suffixed values). */
const parseIsoInLA = (iso: string) => dayjs.utc(iso).tz(LA_TIMEZONE);

/** Inclusive calendar-day count between two instants (immune to endOf('day') time components). */
const getInclusiveDayCount = (start: dayjs.Dayjs, end: dayjs.Dayjs): number =>
  end.startOf('day').diff(start.startOf('day'), 'day') + 1;

export type TimeUnit = 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year';
export type ComparisonType =
  | 'none'
  | 'previous-period'
  | 'previous-month'
  | 'previous-year'
  | 'custom';
export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'last14days'
  | 'last30days'
  | 'last90days'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'thisYear'
  | 'lastYear'
  | 'custom';

export interface DateRange {
  start: string; // ISO date string in UTC
  end: string; // ISO date string in UTC
}

export interface DateRangePickerValue {
  startDate: string;
  endDate: string;
  unit: TimeUnit;
  comparisonStartDate?: string;
  comparisonEndDate?: string;
  comparisonType: ComparisonType;
  presetLabel?: string;
}

export interface DateRangePickerOutput {
  start_date: string;
  end_date: string;
  previous_start_date: string | null;
  previous_end_date: string | null;
  unit: TimeUnit;
  comparison_type: ComparisonType;
  preset_label?: string;
}

export interface CalendarDay {
  date: dayjs.Dayjs;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isInRange: boolean;
  isStartDate: boolean;
  isEndDate: boolean;
  isComparisonRange: boolean;
  isComparisonStart: boolean;
  isComparisonEnd: boolean;
  isDisabled: boolean;
}

export interface UnitSuggestion {
  recommended: string;
  available: string[];
}

export interface PredefinedRange {
  label: string;
  getValue: () => DateRange;
}

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [CommonModule, IconComponentV2, ClickOutsideDirective],
  templateUrl: './date-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    // Note: `pointer-events` is intentionally NOT part of this animation
    // because it's a non-animatable CSS property and Angular logs a
    // warning when it's used inside `style()`. It is instead bound via
    // `[style.pointer-events]` on each element that uses `@slideInOut`
    // so the closed panel cannot capture clicks while it's invisible.
    trigger('slideInOut', [
      state(
        'closed',
        style({
          opacity: 0,
          transform: 'translateY(-8px) scale(0.95)',
        })
      ),
      state(
        'open',
        style({
          opacity: 1,
          transform: 'translateY(0) scale(1)',
        })
      ),
      transition('closed => open', [
        style({
          opacity: 0,
          transform: 'translateY(-8px) scale(0.95)',
        }),
        animate(
          '200ms cubic-bezier(0.23, 1, 0.32, 1)',
          style({
            opacity: 1,
            transform: 'translateY(0) scale(1)',
          })
        ),
      ]),
      transition('open => closed', [
        animate(
          '150ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({
            opacity: 0,
            transform: 'translateY(-8px) scale(0.95)',
          })
        ),
      ]),
    ]),
  ],
})
export class DatePickerComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @ViewChild('containerRef', { static: true }) containerRef!: ElementRef;

  public dayjs = dayjs;
  public commonService = inject(CommonService);
  private auditService = inject(AuditService);
  @ViewChild('startDate', { static: false }) startDate!: ElementRef;
  @ViewChild('endDate', { static: false }) endDate!: ElementRef;
  @ViewChild('triggerRef', { static: false }) triggerRef?: ElementRef;
  @ViewChild('expandedContainer', { static: false }) expandedContainer?: ElementRef;
  @ViewChild('groupByButton', { static: false }) groupByButton?: ElementRef;
  @ViewChild('expandedComparisionContainer', { static: false }) expandedComparisionContainer?: ElementRef;
  @ViewChild('expandedGroupContainer', { static: false }) expandedGroupContainer?: ElementRef;
  @ViewChild('rangeButton', { static: false }) rangeButton?: ElementRef;
  @ViewChild('comparisonButton', { static: false }) comparisonButton?: ElementRef;
  @ViewChild('timeUnitDropdown', { static: false }) timeUnitDropdown?: DropdownComponent;
  bottomSheetRef = inject(MatBottomSheetRef, { optional: true });

  // Configuration Inputs
  @Input() value?: DateRangePickerValue;
  @Input() placeholder = 'Select date range';
  @Input() disabled = false;
  @Input() minDate?: string; // ISO date string
  @Input() maxDate?: string; // ISO date string
  @Input() allowComparison = true;
  @Input() activeUnit: TimeUnit | null = null;
  @Input() auditContext: string = '';
  @Input() showComparison = true;
  @Input() showGroupBy = true;
  @Input() defaultTimeUnit: TimeUnit = 'day';
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() triggerClass?: string;
  @Input() variant: 'default' | 'outline' | 'primary' = 'default';
  @Input() allowedGroupByUnits: TimeUnit[] | null = null;
  @Input() alwaysAvailableUnit: TimeUnit | TimeUnit[] | null = null;
  @Input() closeOnScroll = false;
  @Input() isMobileMode = false;
  @Input() customPredefinedRanges: PredefinedRange[] | null = null;

  private breakpointService = inject(BreakpointService);

  // Responsive mobile state
  isMobile = computed(() => this.isMobileMode || this.breakpointService.isMobile());
  isDateInvalid = signal(false);
  isEndDateInvalid = signal(false);

  // Events
  @Output() valueChange = new EventEmitter<DateRangePickerValue>();
  @Output() openChange = new EventEmitter<boolean>();
  @Output() dateRangeChange = new EventEmitter<DateRangePickerOutput>();
  @Output() onApply = new EventEmitter<DateRangePickerOutput>();
  @Output() onClear = new EventEmitter<void>();

  // Internal State
  isExpanded = signal(false);
  isComparisionExpanded = signal(false);
  isGroupExpanded = signal(false);
  expandedSection = signal<'range' | 'comparison' | 'groupby' | null>(null);

  // Calendar months — start (left) and end (right) panels can show different months
  startCalendarMonth = signal(nowInLA().startOf('month'));
  endCalendarMonth = signal(nowInLA().startOf('month'));
  comparisonCalendarMonth1 = signal(nowInLA());
  comparisonCalendarMonth2 = signal(nowInLA().add(1, 'month'));

  // Selection State
  selectedRange = signal<DateRange>({
    start: nowInLA().subtract(6, 'day').startOf('day').toISOString(),
    end: nowInLA().endOf('day').toISOString(),
  });

  comparisonType = signal<ComparisonType>('none');
  comparisonRange = signal<DateRange | null>(null);
  timeUnit = signal<TimeUnit>(this.defaultTimeUnit);

  selectedPreset = signal<string>('last7days');
  mobileView = signal<'year' | 'month' | 'date' | 'quickSelect'>('date');
  isMobilePresetOpen = signal(false);
  isMobileGroupByOpen = signal(false);

  toggleMobileGroupBy(): void {
    this.isMobileGroupByOpen.update((v) => !v);
  }

  public previousPeriodDates = computed(() => {
    const range = this.selectedRange();
    const start = dayjs.utc(range.start).tz(LA_TIMEZONE);
    const end = dayjs.utc(range.end).tz(LA_TIMEZONE);
    const { start: comparisonStart, end: comparisonEnd } = getCalendarEquivalentPreviousPeriod(start, end);
    return `${this.formatDate(comparisonStart)} - ${this.formatDate(comparisonEnd)}`;
  });

  public previousYearDates = computed(() => {
    const range = this.selectedRange();
    const start = dayjs.utc(range.start).tz(LA_TIMEZONE);
    const end = dayjs.utc(range.end).tz(LA_TIMEZONE);
    const comparisonEnd = end.subtract(1, 'year');
    const comparisonStart = start.subtract(1, 'year');
    return `${this.formatDate(comparisonStart)} - ${this.formatDate(comparisonEnd)}`;
  });

  public previousMonthDates = computed(() => {
    const range = this.selectedRange();
    const start = dayjs.utc(range.start).tz(LA_TIMEZONE);
    const end = dayjs.utc(range.end).tz(LA_TIMEZONE);
    const comparisonEnd = end.subtract(1, 'month');
    const comparisonStart = start.subtract(1, 'month');
    return `${this.formatDate(comparisonStart)} - ${this.formatDate(comparisonEnd)}`;
  });
  isSelectingEndDate = signal(false);
  hoveredDate = signal<dayjs.Dayjs | null>(null);
  isSelectingStart = signal(true);

  // Temporary range for calendar selections (before apply)
  temporaryRange = signal<DateRange | null>(null);
  originalRange = signal<DateRange | null>(null);

  private readonly customPredefinedRangesSig = signal<PredefinedRange[] | null>(null);
  private readonly allowedGroupByUnitsSig = signal<TimeUnit[] | null>(null);

  readonly activePresetList = computed<PredefinedRange[]>(() => {
    const custom = this.customPredefinedRangesSig();
    return custom?.length ? custom : this._defaultPredefinedRanges;
  });

  activePresetLabel = computed(() => {
    const presetId = this.selectedPreset();
    const found = this.activePresetList().find(
      (p) => this.getPresetId(p.label) === presetId
    );
    if (found) return found.label;
    const type = this.selectedType();
    return type ? type : 'Tap to Select';
  });


  // Dropdown States
  isStartMonthOpen = signal(false);
  isStartYearOpen = signal(false);
  isEndMonthOpen = signal(false);
  isEndYearOpen = signal(false);
  dropdownPosition = signal<{ top: number; left: number; width: number; maxHeight: number }>({
    top: -9999,
    left: -9999,
    width: 0,
    maxHeight: 0
  });

  // Computed properties for size-based styling
  triggerPadding = computed(() => {
    switch (this.size) {
      case 'sm':
        return 'py-2';
      case 'md':
        return 'py-3';
      case 'lg':
        return 'py-3.5';
      default:
        return 'py-3';
    }
  });

  triggerGap = computed(() => {
    return this.size === 'sm' ? 'gap-3' : 'gap-6';
  });

  textSize = computed(() => {
    return this.size === 'lg' ? 'text-base' : 'text-sm';
  });

  iconSizeClass = computed(() => {
    return this.size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  });

  get triggerClasses() {
    const minHeightClass = {
      sm: 'min-h-[2rem]',
      md: 'min-h-[2.5rem]',
      lg: 'min-h-[3rem]'
    }[this.size] || 'min-h-[2.5rem]';

    const paddingXClass = {
      sm: 'px-2.5',
      md: 'px-3',
      lg: 'px-4'
    }[this.size] || 'px-3';

    const baseClasses =
      `w-full flex items-center justify-between rounded-lg transition-all duration-300 group border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 text-[#141437] dark:text-white hover:border-purple-400 dark:hover:border-line-strong hover:bg-white dark:hover:bg-surface-1/50 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 dark:focus:border-purple-400 ${minHeightClass}`;

    const panelClasses = this.commonService.isPanelOpen() ? 'gap-2 px-2' : `${this.triggerGap()} ${paddingXClass}`;

    return [baseClasses, panelClasses, this.triggerClass].filter(Boolean).join(' ');
  }

  get containerGap() {
    return this.commonService.isPanelOpen() ? 'gap-2' : this.triggerGap();
  }

  // Listener reference for binding/unbinding
  private repositionListener = () => this.positionPanelUnderTrigger();
  private scrollCloseListener = (event: Event) => {
    const target = event.target as HTMLElement;
    const isInside =
      this.getTriggerElement()?.contains(target) ||
      this.expandedContainer?.nativeElement?.contains(target) ||
      this.expandedComparisionContainer?.nativeElement?.contains(target) ||
      this.expandedGroupContainer?.nativeElement?.contains(target) ||
      document.getElementById('date-picker-range-panel')?.contains(target) ||
      document.getElementById('date-picker-comparison-panel')?.contains(target) ||
      document.getElementById('date-picker-groupby-panel')?.contains(target);

    if (!isInside) {
      this.close();
    }
  };

  // Computed Properties
  displayText = computed(() => {
    const range = this.temporaryRange() || this.selectedRange();
    const start = parseIsoInLA(range.start);
    const end = parseIsoInLA(range.end);
    const days = getInclusiveDayCount(start, end);

    return `${this.formatDate(start)} → ${this.formatDate(end)} (${days}d)`;
  });

  comparisonLabel = computed(() => {
    const type = this.comparisonType();
    if (type === 'none') return 'No Comparison';
    if (type === 'previous-period') return 'Previous Period';
    if (type === 'previous-month') return 'Previous Month';
    if (type === 'previous-year') return 'Previous Year';
    return 'Custom Range';
  });

  startDateOpen() {
    this.startDate.nativeElement.showPicker();
  }

  comparisonDates = computed(() => {
    const range = this.comparisonRange();
    if (!range) return '';
    const start = dayjs.utc(range.start).tz(LA_TIMEZONE).startOf('day');
    const end = dayjs.utc(range.end).tz(LA_TIMEZONE).startOf('day');
    return `${this.formatDate(start)} - ${this.formatDate(end)}`;
  });

  currentMonthDays = computed(() => {
    const displayRange = this.temporaryRange() || this.selectedRange();
    return this.generateCalendarDays(this.startCalendarMonth(), displayRange);
  });

  // iOS perf: nextMonthDays is only rendered in the desktop branch of the
  // template (mobile uses a single calendar pane). Skip the work on mobile
  // - generating 42 calendar-day objects with multiple dayjs comparisons
  // each is one of the heavier per-frame operations in this component.
  nextMonthDays = computed(() => {
    if (this.isMobile()) return [];
    const displayRange = this.temporaryRange() || this.selectedRange();
    return this.generateCalendarDays(this.endCalendarMonth(), displayRange);
  });

  comparisonMonth1Days = computed(() => {
    const range = this.comparisonRange();
    if (!range) return [];
    return this.generateCalendarDays(this.comparisonCalendarMonth1(), range);
  });

  comparisonMonth2Days = computed(() => {
    const range = this.comparisonRange();
    if (!range) return [];
    return this.generateCalendarDays(this.comparisonCalendarMonth2(), range);
  });

  groupByWidth = computed(() => {
    return 240;
  });

  availableTimeUnits = computed(() => {
    const range = this.selectedRange();
    const startTime = dayjs.utc(range.start).valueOf();
    const endTime = dayjs.utc(range.end).valueOf();

    if (isNaN(startTime) || isNaN(endTime)) {
      let base = [
        { label: 'Day', value: 'day' as TimeUnit, icon: 'calendar' as IconName },
        { label: 'Week', value: 'week' as TimeUnit, icon: 'calendar' as IconName },
      ];
      base = this.filterUnits(base);
      return base;
    }

    const diffMs = Math.abs(endTime - startTime);
    const oneDay = 86400000;
    const sevenDays = oneDay * 7;
    const oneMonth = 2629800000;
    const threeMonths = oneMonth * 3;
    const sixMonths = oneMonth * 6;
    const oneYear = oneDay * 365;

    let available: TimeUnit[] = [];

    if (diffMs <= oneDay) {
      available = ['day', 'hour'];
    } else if (diffMs <= sevenDays) {
      available = ['day', 'hour'];
    } else if (diffMs <= oneMonth) {
      available = ['day', 'week'];
    } else if (diffMs <= threeMonths) {
      available = ['week', 'month'];
    } else if (diffMs <= sixMonths) {
      available = ['week', 'month'];
    } else if (diffMs <= oneYear) {
      available = ['month', 'quarter'];
    } else {
      available = ['quarter', 'year'];
    }

    const unitMap: Record<TimeUnit, { label: string; icon: IconName }> = {
      hour: { label: 'Hour', icon: 'calendar' },
      day: { label: 'Day', icon: 'calendar' },
      week: { label: 'Week', icon: 'calendar' },
      month: { label: 'Month', icon: 'calendar' },
      quarter: { label: 'Quarter', icon: 'chartNoAxesCombined' },
      year: { label: 'Year', icon: 'chartNoAxesCombined' },
    };

    const base = available.map((unit) => ({
      label: unitMap[unit].label,
      value: unit,
      icon: unitMap[unit].icon,
    }));
    return this.filterUnits(base);
  });

  groupByOptions = computed(() => {
    const allowed = new Set<TimeUnit>(this.allowedGroupByUnitsSig() ?? []);
    if (this.alwaysAvailableUnit) {
      if (Array.isArray(this.alwaysAvailableUnit)) {
        this.alwaysAvailableUnit.forEach((u) => allowed.add(u));
      } else {
        allowed.add(this.alwaysAvailableUnit);
      }
    }

    const hasRestriction = allowed.size > 0;
    return this.timeUnits.map((u) => ({
      label: u.label,
      value: u.value,
      icon: u.icon,
      disabled: hasRestriction && !allowed.has(u.value),
    }));
  });

  selectedGroupByLabel = computed(() => {
    const found = this.timeUnits.find((u) => u.value === this.timeUnit());
    return found ? found.label : 'Choose Group Type';
  });


  weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  get predefinedRanges(): PredefinedRange[] {
    return this.customPredefinedRanges?.length ? this.customPredefinedRanges : this._defaultPredefinedRanges;
  }

  private _defaultPredefinedRanges: PredefinedRange[] = [
    {
      label: 'Today',
      getValue: () => {
        const today = nowInLA();
        return {
          start: today.startOf('day').toISOString(),
          end: today.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Yesterday',
      getValue: () => {
        const yesterday = nowInLA().subtract(1, 'day');
        return {
          start: yesterday.startOf('day').toISOString(),
          end: yesterday.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last 7 Days',
      getValue: () => ({
        start: nowInLA().subtract(6, 'day').startOf('day').toISOString(),
        end: nowInLA().endOf('day').toISOString(),
      }),
    },
    {
      label: 'Last 14 Days',
      getValue: () => ({
        start: nowInLA().subtract(13, 'day').startOf('day').toISOString(),
        end: nowInLA().endOf('day').toISOString(),
      }),
    },
    {
      label: 'Last 30 Days',
      getValue: () => ({
        start: nowInLA().subtract(29, 'day').startOf('day').toISOString(),
        end: nowInLA().endOf('day').toISOString(),
      }),
    },
    {
      label: 'Last 90 Days',
      getValue: () => ({
        start: nowInLA().subtract(89, 'day').startOf('day').toISOString(),
        end: nowInLA().endOf('day').toISOString(),
      }),
    },
    {
      label: 'This Week',
      getValue: () => {
        const now = nowInLA();
        const startOfWeek = now.startOf('isoWeek');
        return {
          start: startOfWeek.toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Week',
      getValue: () => {
        const now = nowInLA();
        const startOfLastWeek = now.subtract(1, 'week').startOf('isoWeek');
        const endOfLastWeek = now.subtract(1, 'week').endOf('isoWeek');
        return {
          start: startOfLastWeek.toISOString(),
          end: endOfLastWeek.toISOString(),
        };
      },
    },
    {
      label: 'This Month',
      getValue: () => {
        const now = nowInLA();
        return {
          start: now.startOf('month').toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Month',
      getValue: () => {
        const lastMonth = nowInLA().subtract(1, 'month');
        return {
          start: lastMonth.startOf('month').toISOString(),
          end: lastMonth.endOf('month').toISOString(),
        };
      },
    },
    {
      label: 'This Quarter',
      getValue: () => {
        const now = nowInLA();
        const quarter = Math.floor(now.month() / 3);
        const quarterStart = now.month(quarter * 3).startOf('month');
        return {
          start: quarterStart.toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Quarter',
      getValue: () => {
        const now = nowInLA().subtract(3, 'month');
        const quarter = Math.floor(now.month() / 3);
        const quarterStart = now.month(quarter * 3).startOf('month');
        const quarterEnd = now.month(quarter * 3 + 2).endOf('month');
        return {
          start: quarterStart.toISOString(),
          end: quarterEnd.toISOString(),
        };
      },
    },
    {
      label: 'This Year',
      getValue: () => {
        const now = nowInLA();
        return {
          start: now.startOf('year').toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Year',
      getValue: () => {
        const lastYear = nowInLA().subtract(1, 'year');
        return {
          start: lastYear.startOf('year').toISOString(),
          end: lastYear.endOf('year').toISOString(),
        };
      },
    },
    {
      label: 'Last 2 years',
      getValue: () => {
        const last2Years = nowInLA().subtract(2, 'year');
        return {
          start: last2Years.startOf('year').toISOString(),
          end: nowInLA().endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last 3 years',
      getValue: () => {
        const last3Years = nowInLA().subtract(3, 'year');
        return {
          start: last3Years.startOf('year').toISOString(),
          end: nowInLA().endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last 5 years',
      getValue: () => {
        const last5Years = nowInLA().subtract(5, 'year');
        return {
          start: last5Years.startOf('year').toISOString(),
          end: nowInLA().endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'All Time',
      getValue: () => ({
        start: '2017-01-01T00:00:00.000Z',
        end: nowInLA().endOf('day').toISOString(),
      }),
    },
  ];

  timeUnits: Array<{ value: TimeUnit; label: string; icon: IconName }> = [
    { value: 'hour', label: 'Hour', icon: 'calendar' },
    { value: 'day', label: 'Day', icon: 'calendar' },
    { value: 'week', label: 'Week', icon: 'calendar' },
    { value: 'month', label: 'Month', icon: 'calendar' },
    { value: 'quarter', label: 'Quarter', icon: 'chartNoAxesCombined' },
    { value: 'year', label: 'Year', icon: 'chartNoAxesCombined' },
  ];

  timeUnitOptions: DropdownOption[] = this.timeUnits.map((unit) => ({
    value: unit.value,
    label: unit.label,
    icon: unit.icon,
  }));

  public availableUnitIndexes: any[] = this.timeUnits.map((_, i) => i);

  ngOnInit(): void {
    this.customPredefinedRangesSig.set(this.customPredefinedRanges ?? null);
    this.setDefaultRange();
    this.initializeFromValue();
    this.emitDateRangeChange();
  }

  private setDefaultRange(): void {
    const now = nowInLA();
    if (this.isMobileMode) {
      this.selectedRange.set({
        start: now.subtract(6, 'day').startOf('day').format(),
        end: now.endOf('day').format(),
      });
      this.selectedPreset.set('last7days');
      this.selectedType.set('Last 7 Days');
      this.updateCalendarMonths(now.subtract(6, 'day'), now);
    } else {
      this.selectedRange.set({
        start: now.subtract(6, 'day').startOf('day').format(),
        end: now.endOf('day').format(),
      });
      this.selectedPreset.set('last7days');
      this.selectedType.set('Last 7 Days');
      this.updateCalendarMonths(now.subtract(6, 'day'), now);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customPredefinedRanges']) {
      this.customPredefinedRangesSig.set(this.customPredefinedRanges ?? null);
    }
    if (changes['allowedGroupByUnits']) {
      this.allowedGroupByUnitsSig.set(this.allowedGroupByUnits ?? null);
    }
    if (changes['value'] && !changes['value'].firstChange) {
      this.initializeFromValue();
    }
  }

  ngAfterViewInit(): void {
    if (this.timeUnitDropdown) {
      this.timeUnitDropdown.writeValue(this.timeUnit());
    }
  }

  private portalRoot: HTMLElement | null = null;

  private attachToBody(panel?: HTMLElement): void {
    if (this.isMobile()) return;
    try {
      if (!this.portalRoot) {
        this.portalRoot = document.createElement('div');
        this.portalRoot.style.position = 'absolute';
        this.portalRoot.style.top = '0';
        this.portalRoot.style.left = '0';
        this.portalRoot.style.width = '100%';
        this.portalRoot.style.pointerEvents = 'none';
        this.portalRoot.style.zIndex = '9999';
      }

      if (!document.body.contains(this.portalRoot)) {
        document.body.appendChild(this.portalRoot);
      }

      const panelToAttach = panel || this.expandedContainer?.nativeElement;
      if (panelToAttach && this.portalRoot) {
        this.portalRoot.appendChild(panelToAttach);
        panelToAttach.style.pointerEvents = 'auto';
      }
    } catch (e) {
      console.error('Error attaching to body:', e);
    }
  }

  private getTriggerElement(): HTMLElement | undefined {
    return (
      this.triggerRef?.nativeElement ||
      this.containerRef?.nativeElement?.querySelector('button') ||
      this.containerRef?.nativeElement
    ) as HTMLElement | undefined;
  }

  private getPositionAnchorElement(section: string | null): HTMLElement | undefined {
    if (section === 'comparison') {
      return (this.comparisonButton?.nativeElement || this.getTriggerElement()) as HTMLElement;
    }
    if (section === 'groupby') {
      return (this.groupByButton?.nativeElement || this.getTriggerElement()) as HTMLElement;
    }
    return this.getTriggerElement();
  }

  private getMinPanelLeft(margin: number): number {
    const sidebar = document.querySelector('app-sidebar');
    if (sidebar) {
      return Math.max(margin, sidebar.getBoundingClientRect().right + margin);
    }
    return margin;
  }

  private resetDropdownPositionForOpen(section: string | null): void {
    const width = section === 'range' ? 864 : section === 'groupby' ? 200 : 450;
    this.dropdownPosition.update((current) => ({
      ...current,
      width,
      maxHeight: Math.max(current.maxHeight, 500),
    }));
  }

  private positionPanelUnderTrigger(): void {
    try {
      const section = this.expandedSection();
      const anchor = this.getPositionAnchorElement(section);
      const trigger = this.getTriggerElement();
      let panel: HTMLElement | undefined;

      if (section === 'range') {
        panel = this.expandedContainer?.nativeElement || document.getElementById('date-picker-range-panel') as HTMLElement;
      } else if (section === 'comparison') {
        panel = this.expandedComparisionContainer?.nativeElement || document.getElementById('date-picker-comparison-panel') as HTMLElement;
      } else if (section === 'groupby') {
        panel = this.expandedGroupContainer?.nativeElement || document.getElementById('date-picker-groupby-panel') as HTMLElement;
      }

      if (!anchor || !trigger || !panel) {
        if (this.isExpanded() || this.isComparisionExpanded() || this.isGroupExpanded()) {
          setTimeout(() => this.positionPanelUnderTrigger(), 100);
        }
        return;
      }

      const anchorRect = anchor.getBoundingClientRect();
      const triggerRect = trigger.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gap = 8;
      const margin = 16;
      const minLeft = this.getMinPanelLeft(margin);
      const alignRight = section === 'comparison' || section === 'groupby';

      let panelWidth = 0;
      if (section === 'range') {
        panelWidth = Math.min(864, viewportWidth - (margin * 2));
      } else {
        const measuredWidth = panelRect.width || panel.offsetWidth;
        panelWidth = measuredWidth > 10 ? measuredWidth : (section === 'groupby' ? 200 : 450);
      }

      let left = alignRight
        ? triggerRect.right - panelWidth
        : triggerRect.left;

      if (!alignRight && left + panelWidth > viewportWidth - margin) {
        left = Math.max(minLeft, triggerRect.right - panelWidth);
      }

      if (left < minLeft) {
        left = minLeft;
      }

      if (left + panelWidth > viewportWidth - margin) {
        left = Math.max(minLeft, viewportWidth - margin - panelWidth);
      }

      let panelHeight = panelRect.height || panel.offsetHeight;
      if (!panelHeight || panelHeight < 10) {
        panelHeight = section === 'range' ? 500 : 300;
      }

      let top = anchorRect.bottom + gap;
      let maxHeight = Math.max(200, viewportHeight - top - margin);

      if (top + panelHeight > viewportHeight - margin) {
        const altTop = anchorRect.top - gap - panelHeight;
        const altMaxHeight = anchorRect.top - gap - margin;

        if (altTop >= margin) {
          top = altTop;
          maxHeight = Math.max(200, panelHeight);
        } else if (altMaxHeight > (viewportHeight - top - margin)) {
          top = margin;
          maxHeight = Math.max(200, altMaxHeight);
        } else {
          top = anchorRect.bottom + gap;
          maxHeight = Math.max(200, viewportHeight - top - margin);
        }
      }

      this.dropdownPosition.set({
        top,
        left,
        width: panelWidth,
        maxHeight
      });

      if (this.portalRoot) {
        panel.style.zIndex = '9999';
      }
    } catch (e) {
      console.error('Error repositioning date-picker panel:', e);
    }
  }

  private addScrollListeners(): void {
    const listener = this.closeOnScroll ? this.scrollCloseListener : this.repositionListener;
    document.addEventListener('scroll', listener, true);
    window.addEventListener('resize', this.repositionListener);
  }

  private removeScrollListeners(): void {
    const listener = this.closeOnScroll ? this.scrollCloseListener : this.repositionListener;
    document.removeEventListener('scroll', listener, true);
    window.removeEventListener('resize', this.repositionListener);
  }

  ngOnDestroy(): void {
    this.removeScrollListeners();
    if (this.portalRoot && document.body.contains(this.portalRoot)) {
      document.body.removeChild(this.portalRoot);
    }
  }

  toggle(): void {
    if (this.disabled) return;
    this.isComparisionExpanded.set(false);
    this.isGroupExpanded.set(false);

    const wasExpanded = this.isExpanded();
    this.isExpanded.set(!wasExpanded);

    if (this.isExpanded()) {
      this.expandedSection.set('range');
      this.syncCalendarMonthsToActiveRange();
      this.resetDropdownPositionForOpen('range');
      setTimeout(() => {
        this.attachToBody();
        this.positionPanelUnderTrigger();
        this.addScrollListeners();
      }, 0);
      this.closeAllDropdowns();
    } else {
      this.expandedSection.set(null);
      this.removeScrollListeners();
      this.dropdownPosition.set({ top: -9999, left: -9999, width: 0, maxHeight: 0 });
    }

    this.openChange.emit(this.isExpanded());
  }

  toggleComparision(): void {
    if (this.disabled) return;
    this.isExpanded.set(false);
    this.isGroupExpanded.set(false);

    const wasExpanded = this.isComparisionExpanded();
    this.isComparisionExpanded.set(!wasExpanded);

    if (this.isComparisionExpanded()) {
      this.expandedSection.set('comparison');
      this.resetDropdownPositionForOpen('comparison');
      setTimeout(() => {
        const panel = this.expandedComparisionContainer?.nativeElement || document.getElementById('date-picker-comparison-panel');
        if (panel) this.attachToBody(panel);
        this.positionPanelUnderTrigger();
        this.addScrollListeners();
      }, 0);
      this.closeAllDropdowns();
    } else {
      this.expandedSection.set(null);
      this.removeScrollListeners();
      this.dropdownPosition.set({ top: -9999, left: -9999, width: 0, maxHeight: 0 });
    }

    this.openChange.emit(this.isComparisionExpanded());
  }

  toggleGroup(): void {
    if (this.disabled) return;
    this.isExpanded.set(false);
    this.isComparisionExpanded.set(false);

    const wasExpanded = this.isGroupExpanded();
    this.isGroupExpanded.set(!wasExpanded);

    if (this.isGroupExpanded()) {
      this.expandedSection.set('groupby');
      this.resetDropdownPositionForOpen('groupby');
      setTimeout(() => {
        const panel = this.expandedGroupContainer?.nativeElement || document.getElementById('date-picker-groupby-panel');
        if (panel) this.attachToBody(panel);
        this.positionPanelUnderTrigger();
        this.addScrollListeners();
      }, 0);
      this.closeAllDropdowns();
    } else {
      this.expandedSection.set(null);
      this.removeScrollListeners();
      this.dropdownPosition.set({ top: -9999, left: -9999, width: 0, maxHeight: 0 });
    }

    this.openChange.emit(this.isGroupExpanded());
  }

  close(): void {
    if (this.isMobile() && this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
      return;
    }
    const originalRange = this.originalRange();
    if (originalRange && this.temporaryRange()) {
      this.selectedRange.set(originalRange);
    }

    this.temporaryRange.set(null);
    this.originalRange.set(null);
    this.isSelectingStart.set(true);
    this.syncCalendarMonthsToActiveRange();

    this.removeScrollListeners();

    this.isExpanded.set(false);
    this.isGroupExpanded.set(false);
    this.isComparisionExpanded.set(false);
    this.expandedSection.set(null);
    this.closeAllDropdowns();
    this.openChange.emit(false);
  }

  calculatePosition(): void {
    this.positionPanelUnderTrigger();
  }

  closeAllDropdowns(): void {
    this.isStartMonthOpen.set(false);
    this.isStartYearOpen.set(false);
    this.isEndMonthOpen.set(false);
    this.isEndYearOpen.set(false);
  }

  onClickOutside(): void {
    if (this.isMobile() && this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
    } else {
      this.close();
    }
  }

  get portalPanels(): HTMLElement[] {
    return [
      'date-picker-range-panel',
      'date-picker-comparison-panel',
      'date-picker-groupby-panel',
    ]
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
  }

  toggleStartMonthDropdown(): void {
    const wasOpen = this.isStartMonthOpen();
    this.closeAllDropdowns();
    this.isStartMonthOpen.set(!wasOpen);
  }

  toggleStartYearDropdown(): void {
    const wasOpen = this.isStartYearOpen();
    this.closeAllDropdowns();
    this.isStartYearOpen.set(!wasOpen);
  }

  toggleEndMonthDropdown(): void {
    const wasOpen = this.isEndMonthOpen();
    this.closeAllDropdowns();
    this.isEndMonthOpen.set(!wasOpen);
  }

  toggleEndYearDropdown(): void {
    const wasOpen = this.isEndYearOpen();
    this.closeAllDropdowns();
    this.isEndYearOpen.set(!wasOpen);
  }

  setStartMonthAndClose(month: number): void {
    if (!this.isStartMonthValid(month)) return;

    const newDate = this.startCalendarMonth().month(month).startOf('month');
    this.startCalendarMonth.set(newDate);
    if (newDate.isAfter(this.endCalendarMonth(), 'month')) {
      this.endCalendarMonth.set(newDate);
    }
    this.isStartMonthOpen.set(false);
  }

  setStartYearAndClose(year: number): void {
    if (!this.isStartYearValid(year)) return;

    const newDate = this.startCalendarMonth().year(year).startOf('month');
    this.startCalendarMonth.set(newDate);
    if (newDate.isAfter(this.endCalendarMonth(), 'month')) {
      this.endCalendarMonth.set(newDate);
    }
    this.isStartYearOpen.set(false);
  }

  toggleSection(section: 'range' | 'comparison' | 'groupby'): void {
    this.expandedSection.set(section);
  }

  selectedType = signal<string>('Last 7 Days');

  selectPreset(preset: PredefinedRange): void {
    this.selectedType.set(preset.label);
    const range = preset.getValue();
    this.selectedRange.set(range);
    this.temporaryRange.set(null);
    this.selectedPreset.set(this.getPresetId(preset.label));
    this.updateCalendarMonths(
      dayjs.tz(range.start, LA_TIMEZONE),
      dayjs.tz(range.end, LA_TIMEZONE)
    );
    this.autoSelectTimeUnit();
    this.updateComparison();
    if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(this.getOutput());
    } else {
      this.close();
      this.emitValue();
      this.emitDateRangeChange();
    }
  }

  private autoSelectTimeUnit(): void {
    const availableUnits = this.availableTimeUnits();
    if (availableUnits.length > 0) {
      const firstUnit = availableUnits[0].value;
      if (firstUnit === 'hour' && availableUnits.length > 1) {
        this.timeUnit.set(availableUnits[1].value);
      } else {
        this.timeUnit.set(firstUnit);
      }
    }
  }

  dateRange = signal<{ startDate: string; endDate: string; unit: string }>({
    startDate: '',
    endDate: '',
    unit: '',
  });

  calculateDateRange(unit: TimeUnit): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let start: Date;
    let end: Date;

    switch (unit) {
      case 'hour':
        start = new Date(today);
        start.setHours(today.getHours() - 1);
        end = new Date(today);
        break;
      case 'day':
        start = new Date(today);
        end = new Date(today);
        break;
      case 'week':
        const dayOfWeek = today.getDay();
        start = new Date(today);
        start.setDate(today.getDate() - dayOfWeek);
        end = new Date(today);
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today);
        break;
      case 'quarter':
        const currentMonth = today.getMonth();
        const startMonth = currentMonth - (currentMonth % 3);
        start = new Date(today.getFullYear(), startMonth, 1);
        end = new Date(today);
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today);
        break;
      default:
        start = new Date(today);
        end = new Date(today);
        break;
    }

    this.dateRange.set({
      startDate: dayjs(start).format('MM-DD-YYYY'),
      endDate: dayjs(end).format('MM-DD-YYYY'),
      unit: unit,
    });
  }

  onTimeUnitChange(unit: string, index: number): void {
    this.timeUnit.set(unit as TimeUnit);
    this.selectedUnitIndex = index;
    this.updateComparison();
    this.calculateDateRange(unit as TimeUnit);
    this.isGroupExpanded.set(false);
    this.expandedSection.set(null);
    this.emitValue();
    this.emitDateRangeChange();
  }

  setComparisonType(type: ComparisonType): void {
    this.comparisonType.set(type);
    if (type === 'none') {
      this.comparisonRange.set(null);
    } else if (type === 'custom') {
      if (!this.comparisonRange()) {
        const now = dayjs.utc();
        this.comparisonRange.set({
          start: now.subtract(1, 'month').toISOString(),
          end: now.subtract(1, 'week').toISOString(),
        });
      }
    } else {
      this.updateComparison();
    }
    this.isComparisionExpanded.set(false);
    this.expandedSection.set(null);
    this.emitValue();
    this.emitDateRangeChange();
  }

  applyRange(): void {
    const tempRange = this.temporaryRange();
    if (tempRange) {
      this.selectedRange.set(tempRange);
      this.autoSelectTimeUnit();
      this.updateComparison();
      this.setMatchingPreset(tempRange.start, tempRange.end);
      this.temporaryRange.set(null);
      this.originalRange.set(null);
    }

    if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(this.getOutput());
    } else {
      this.close();
      this.emitValue();
      this.emitDateRangeChange();
    }
  }

  private getOutput(): DateRangePickerOutput {
    const range = this.selectedRange();
    const compRange = this.comparisonRange() || { start: null, end: null };
    return {
      start_date: range.start,
      end_date: range.end,
      previous_start_date: compRange.start,
      previous_end_date: compRange.end,
      unit: this.timeUnit(),
      comparison_type: this.comparisonType(),
      preset_label: this.selectedType(),
    };
  }

  clearRange(): void {
    this.selectedRange.set({ start: null as any, end: null as any });
    this.comparisonRange.set({ start: null as any, end: null as any });
    this.temporaryRange.set(null);
    this.onClear.emit();
    if (this.isMobile() && this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
    } else {
      this.isExpanded.set(false);
    }
  }

  applyComparison(): void {
    this.updateComparison();
    this.isComparisionExpanded.set(false);
    this.emitValue();
    this.emitDateRangeChange();
  }

  previousStartMonth(): void {
    this.startCalendarMonth.set(this.startCalendarMonth().subtract(1, 'month'));
  }

  nextStartMonth(): void {
    this.startCalendarMonth.set(this.startCalendarMonth().add(1, 'month'));
  }

  previousEndMonth(): void {
    this.endCalendarMonth.set(this.endCalendarMonth().subtract(1, 'month'));
  }

  nextEndMonth(): void {
    this.endCalendarMonth.set(this.endCalendarMonth().add(1, 'month'));
  }

  changeMonth(delta: number): void {
    const newMonth = this.startCalendarMonth().add(delta, 'month');
    this.startCalendarMonth.set(newMonth);
    this.endCalendarMonth.set(newMonth);
  }

  setCalendarMonth(month: number, year: number, isEnd = false): void {
    const newDate = nowInLA().year(year).month(month).startOf('month');
    if (isEnd) {
      this.endCalendarMonth.set(newDate);
      if (newDate.isBefore(this.startCalendarMonth(), 'month')) {
        this.startCalendarMonth.set(newDate);
      }
    } else {
      this.startCalendarMonth.set(newDate);
      if (newDate.isAfter(this.endCalendarMonth(), 'month')) {
        this.endCalendarMonth.set(newDate);
      }
    }
  }

  setComparisonCalendarMonth(month: number, year: number, isSecond = false): void {
    const newDate = nowInLA().year(year).month(month).startOf('month');
    if (isSecond) {
      this.comparisonCalendarMonth2.set(newDate);
    } else {
      this.comparisonCalendarMonth1.set(newDate);
    }
  }

  setComparisonRange(range: DateRange): void {
    this.comparisonRange.set(range);
  }

  onDayClick(date: dayjs.Dayjs, isComparison = false): void {
    if (this.isDateDisabled(date)) return;
    if (isComparison) {
      this.handleComparisonDateClick(date);
    } else {
      this.handleMainDateClick(date);
    }
  }

  onDateHover(date: dayjs.Dayjs): void {
    if (!this.isSelectingStart()) {
      this.hoveredDate.set(date);
    }
  }

  clearHover(): void {
    this.hoveredDate.set(null);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: any): void {
    if (
      this.isStartMonthOpen() ||
      this.isStartYearOpen() ||
      this.isEndMonthOpen() ||
      this.isEndYearOpen()
    ) {
      this.closeAllDropdowns();
      event.preventDefault();
      return;
    }
    if (
      this.isExpanded() ||
      this.isComparisionExpanded() ||
      this.isGroupExpanded()
    ) {
      this.close();
      event.preventDefault();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      !this.isExpanded() &&
      !this.isComparisionExpanded() &&
      !this.isGroupExpanded()
    ) {
      return;
    }

    const target = event.target as Element;
    if (!target) return;

    const clickedInsideTrigger = this.getTriggerElement()?.contains(target);
    const clickedInsideExpanded = this.expandedContainer?.nativeElement?.contains(target);
    const clickedInsideComparison = this.expandedComparisionContainer?.nativeElement?.contains(target);
    const clickedInsideGroup = this.expandedGroupContainer?.nativeElement?.contains(target);

    if (
      !clickedInsideTrigger &&
      !clickedInsideExpanded &&
      !clickedInsideComparison &&
      !clickedInsideGroup
    ) {
      this.close();
    }
  }

  public reset(): void {
    const last7Days = this.predefinedRanges.find(p => p.label === 'Last 7 Days');
    if (last7Days) {
      this.selectPreset(last7Days);
    }
  }

  private initializeFromValue(): void {
    if (this.value) {
      const hasValidStart = !!this.value.startDate && parseIsoInLA(this.value.startDate).isValid();
      const hasValidEnd = !!this.value.endDate && parseIsoInLA(this.value.endDate).isValid();

      if (!hasValidStart || !hasValidEnd) {
        if (this.value.presetLabel) {
          this.selectedType.set(this.value.presetLabel);
          this.selectedPreset.set(this.getPresetId(this.value.presetLabel));
        }
        if (this.value.unit) {
          this.timeUnit.set(this.value.unit);
        }
        if (this.value.comparisonType) {
          this.comparisonType.set(this.value.comparisonType);
        }
        return;
      }

      if (this.value.presetLabel) {
        this.selectedType.set(this.value.presetLabel);
        this.selectedPreset.set(this.getPresetId(this.value.presetLabel));
      } else {
        this.setMatchingPreset(this.value.startDate, this.value.endDate);
      }

      this.selectedRange.set({
        start: parseIsoInLA(this.value.startDate).startOf('day').utc().toISOString(),
        end: parseIsoInLA(this.value.endDate).endOf('day').utc().toISOString(),
      });
      this.timeUnit.set(this.value.unit);
      this.comparisonType.set(this.value.comparisonType);

      if (this.value.comparisonStartDate && this.value.comparisonEndDate) {
        this.comparisonRange.set({
          start: this.value.comparisonStartDate,
          end: this.value.comparisonEndDate,
        });
      }

      const startDate = parseIsoInLA(this.value.startDate);
      const endDate = parseIsoInLA(this.value.endDate);
      this.updateCalendarMonths(startDate, endDate);
    } else {
      const defaultRange = this.selectedRange();
      this.setMatchingPreset(defaultRange.start, defaultRange.end);
    }
  }

  private setMatchingPreset(start: string, end: string): void {
    const matchingPreset = this.predefinedRanges.find((preset) => {
      const range = preset.getValue();
      const pStart = parseIsoInLA(range.start).format('YYYY-MM-DD');
      const pEnd = parseIsoInLA(range.end).format('YYYY-MM-DD');
      const sStart = parseIsoInLA(start).format('YYYY-MM-DD');
      const sEnd = parseIsoInLA(end).format('YYYY-MM-DD');
      return pStart === sStart && pEnd === sEnd;
    });

    if (matchingPreset) {
      this.selectedType.set(matchingPreset.label);
      this.selectedPreset.set(this.getPresetId(matchingPreset.label));
    } else {
      this.selectedType.set('');
      this.selectedPreset.set('custom');
    }
  }

  private setStartCalendarMonth(date: dayjs.Dayjs): void {
    this.startCalendarMonth.set(date.startOf('month'));
  }

  private updateCalendarMonths(startDate: dayjs.Dayjs, endDate?: dayjs.Dayjs): void {
    const startMonth = startDate.startOf('month');
    const endMonth = (endDate ?? startDate).startOf('month');

    if (this.isMobile()) {
      this.startCalendarMonth.set(startMonth);
      this.endCalendarMonth.set(startMonth);
      return;
    }

    if (startMonth.isSame(endMonth, 'month')) {
      this.startCalendarMonth.set(startMonth);
      this.endCalendarMonth.set(startMonth);
    } else {
      this.startCalendarMonth.set(startMonth);
      this.endCalendarMonth.set(endMonth);
    }
  }

  private syncCalendarMonthsToActiveRange(): void {
    const range = this.temporaryRange() || this.selectedRange();
    if (!range?.start || !range?.end) return;

    this.updateCalendarMonths(
      dayjs.utc(range.start).tz(LA_TIMEZONE),
      dayjs.utc(range.end).tz(LA_TIMEZONE)
    );
  }

  private handleMainDateClick(date: dayjs.Dayjs): void {
    if (this.isSelectingStart() && !this.originalRange()) {
      this.originalRange.set(this.selectedRange());
    }

    const normalizedDate = date.startOf('day');

    if (this.isMobile()) {
      this.handleMobileDateClick(normalizedDate);
      return;
    }

    if (this.isSelectingStart()) {
      this.selectedPreset.set('custom');
      this.selectedType.set('Custom');
      const tempRange = {
        start: normalizedDate.utc().toISOString(),
        end: normalizedDate.utc().toISOString(),
      };
      this.temporaryRange.set(tempRange);
      this.isSelectingStart.set(false);
      this.setStartCalendarMonth(normalizedDate);
    } else {
      this.selectedPreset.set('custom');
      this.selectedType.set('Custom');

      const currentTempRange = this.temporaryRange() || this.selectedRange();
      const start = dayjs.utc(currentTempRange.start).tz(LA_TIMEZONE);

      let finalRange: DateRange;
      if (normalizedDate.isSameOrAfter(start, 'day')) {
        finalRange = {
          start: currentTempRange.start,
          end: normalizedDate.utc().toISOString(),
        };
      } else {
        finalRange = {
          start: normalizedDate.utc().toISOString(),
          end: currentTempRange.start,
        };
      }
      this.temporaryRange.set(finalRange);
      this.isSelectingStart.set(true);
    }
  }

  /**
   * Mobile-specific date click handler.
   * Respects which field (Start Date / End Date) the user is editing
   * so that picking an end date never silently replaces the start date
   * (and vice-versa).
   */
  private handleMobileDateClick(normalizedDate: dayjs.Dayjs): void {
    this.selectedPreset.set('custom');
    this.selectedType.set('Custom');

    const currentRange = this.temporaryRange() || this.selectedRange();
    const currentStart = dayjs.utc(currentRange.start).tz(LA_TIMEZONE).startOf('day');
    const currentEnd = dayjs.utc(currentRange.end).tz(LA_TIMEZONE).startOf('day');

    let newStart = currentStart;
    let newEnd = currentEnd;

    if (this.isSelectingStart()) {
      // Editing the Start Date: only update start. 
      newStart = normalizedDate;
      if (newStart.isAfter(currentEnd, 'day')) {
        newEnd = newStart;
      }
      // Auto-advance the user to the End Date field for the next tap.
      this.isSelectingStart.set(false);
    } else {
      // Editing the End Date: only update end. 
      newEnd = normalizedDate;
      if (newEnd.isBefore(currentStart, 'day')) {
        newStart = newEnd;
      }
    }

    const finalRange: DateRange = {
      start: newStart.utc().toISOString(),
      end: newEnd.utc().toISOString(),
    };

    this.temporaryRange.set(finalRange);
    this.selectedRange.set(finalRange);
  }

  private handleComparisonDateClick(date: dayjs.Dayjs): void {
    const currentRange = this.comparisonRange();
    if (!currentRange) {
      const normalizedDate = date.startOf('day');
      this.comparisonRange.set({
        start: normalizedDate.utc().toISOString(),
        end: normalizedDate.utc().toISOString(),
      });
      return;
    }

    const start = dayjs.utc(currentRange.start).tz(LA_TIMEZONE);
    const normalizedDate = date.startOf('day');

    if (normalizedDate.isSameOrAfter(start, 'day')) {
      this.comparisonRange.set({
        ...currentRange,
        end: normalizedDate.utc().toISOString(),
      });
    } else {
      this.comparisonRange.set({
        start: normalizedDate.utc().toISOString(),
        end: currentRange.start,
      });
    }
  }

  private generateCalendarDays(month: dayjs.Dayjs, range: DateRange): CalendarDay[] {
    const days: CalendarDay[] = [];
    const startOfMonth = month.startOf('month');
    const endOfMonth = month.endOf('month');
    const startOfCalendar = startOfMonth.startOf('isoWeek').startOf('day');
    const endOfCalendar = endOfMonth.endOf('isoWeek').endOf('day');

    let current = startOfCalendar;
    const rangeStart = dayjs.utc(range.start).tz(LA_TIMEZONE).startOf('day');
    const rangeEnd = dayjs.utc(range.end).tz(LA_TIMEZONE).startOf('day');

    while (current.isSameOrBefore(endOfCalendar)) {
      days.push(this.createCalendarDay(current, month, rangeStart, rangeEnd));
      current = current.add(1, 'day');
    }
    return days;
  }

  private createCalendarDay(
    date: dayjs.Dayjs,
    month: dayjs.Dayjs,
    rangeStart: dayjs.Dayjs,
    rangeEnd: dayjs.Dayjs
  ): CalendarDay {
    const compRange = this.comparisonRange();
    const compStart = compRange ? dayjs.utc(compRange.start).tz(LA_TIMEZONE).startOf('day') : null;
    const compEnd = compRange ? dayjs.utc(compRange.end).tz(LA_TIMEZONE).startOf('day') : null;
    const today = nowInLA().startOf('day');
    const isMobile = this.isMobile();
    const hovered = isMobile ? null : this.hoveredDate();

    const normalizedDate = date.startOf('day');
    let previewStart = rangeStart;
    let previewEnd = rangeEnd;

    if (!isMobile && !this.isSelectingStart() && hovered) {
      const normalizedHovered = hovered.startOf('day');
      if (normalizedHovered.isSameOrAfter(rangeStart, 'day')) {
        previewEnd = normalizedHovered;
      } else {
        previewStart = normalizedHovered;
        previewEnd = rangeStart;
      }
    }
    return {
      date,
      isCurrentMonth: normalizedDate.month() === month.month() && normalizedDate.year() === month.year(),
      isToday: normalizedDate.format('YYYY-MM-DD') === today.format('YYYY-MM-DD'),
      isSelected: normalizedDate.isSame(previewStart, 'day') || normalizedDate.isSame(previewEnd, 'day'),
      isInRange: normalizedDate.isAfter(previewStart, 'day') && normalizedDate.isBefore(previewEnd, 'day'),
      isStartDate: normalizedDate.isSame(previewStart, 'day'),
      isEndDate: normalizedDate.isSame(previewEnd, 'day'),
      isComparisonRange: !!(compStart && compEnd && normalizedDate.isAfter(compStart, 'day') && normalizedDate.isBefore(compEnd, 'day')),
      isComparisonStart: !!(compStart && normalizedDate.isSame(compStart, 'day')),
      isComparisonEnd: !!(compEnd && normalizedDate.isSame(compEnd, 'day')),
      isDisabled: this.isDateDisabled(date),
    };
  }

  private isDateDisabled(date: dayjs.Dayjs): boolean {
    const today = nowInLA().startOf('day');
    if (date.isAfter(today, 'day')) {
      return true;
    }
    if (this.minDate && date.isBefore(dayjs.utc(this.minDate), 'day')) {
      return true;
    }
    if (this.maxDate && date.isAfter(dayjs.utc(this.maxDate), 'day')) {
      return true;
    }
    if (this.isMobile() && !this.isSelectingStart()) {
      const lower = this.getMobileEndLowerBoundDate();
      if (lower && date.startOf('day').isBefore(lower, 'day')) return true;
    }
    if (this.isMobile() && !this.isSelectingStart()) {
      const lower = this.getMobileEndLowerBoundDate();
      if (lower && date.startOf('day').isBefore(lower, 'day')) return true;
    }
    return false;
  }


  private getMobileEndLowerBoundDate(): dayjs.Dayjs | null {
    const range = this.temporaryRange() || this.selectedRange();
    if (!range?.start) return null;
    return dayjs.utc(range.start).tz(LA_TIMEZONE).startOf('day');
  }

  isStartMonthValid(monthIndex: number): boolean {
    const todayLA = nowInLA();
    const proposed = nowInLA()
      .year(this.startCalendarMonth().year())
      .month(monthIndex)
      .startOf('month');

    if (proposed.isAfter(todayLA, 'month')) return false;
    if (proposed.isBefore(this.MIN_DATE, 'month')) return false;
    return true;
  }

  isStartYearValid(year: number): boolean {
    const todayLA = nowInLA();
    const proposed = nowInLA()
      .year(year)
      .month(this.startCalendarMonth().month())
      .startOf('month');

    if (proposed.isAfter(todayLA, 'month')) return false;
    if (proposed.isBefore(this.MIN_DATE, 'month')) return false;
    return true;
  }

  /** Month-grid enable check for the mobile picker. Combines the
   *  existing "not in the future" rule with the End-side lower-bound
   *  rule, so End can never navigate to a month earlier than Start.
   */
  isMobileMonthValid(monthIndex: number): boolean {
    if (!this.isStartMonthValid(monthIndex)) return false;
    if (!this.isSelectingStart()) {
      const lower = this.getMobileEndLowerBoundDate();
      if (lower) {
        const browseYear = this.startCalendarMonth().year();
        const lowerYear = lower.year();
        const lowerMonth = lower.month();
        if (browseYear < lowerYear) return false;
        if (browseYear === lowerYear && monthIndex < lowerMonth) return false;
      }
    }
    return true;
  }

  /** Year-grid enable check for the mobile picker. Future years are
   *  always blocked; on the End side, years strictly before the Start
   *  anchor are blocked as well.
   */
  isMobileYearValid(year: number): boolean {
    const todayLA = nowInLA();
    if (year > todayLA.year()) return false;
    if (!this.isSelectingStart()) {
      const lower = this.getMobileEndLowerBoundDate();
      if (lower && year < lower.year()) return false;
    }
    return true;
  }

  private updateComparison(): void {
    const type = this.comparisonType();
    console.log('[date-picker] updateComparison() called, comparisonType =', type);
    if (type === 'none') {
      this.comparisonRange.set(null);
      return;
    }

    const range = this.selectedRange();
    const start = dayjs.utc(range.start).tz(LA_TIMEZONE);
    const end = dayjs.utc(range.end).tz(LA_TIMEZONE);

    if (type === 'previous-period') {
      const { start: comparisonStart, end: comparisonEnd } = getCalendarEquivalentPreviousPeriod(
        start.startOf('day'),
        end.startOf('day')
      );
      this.comparisonRange.set({
        start: comparisonStart.startOf('day').utc().toISOString(),
        end: comparisonEnd.endOf('day').utc().toISOString(),
      });
    } else if (type === 'previous-month') {
      this.comparisonRange.set({
        start: start.subtract(1, 'month').startOf('day').utc().toISOString(),
        end: end.subtract(1, 'month').endOf('day').utc().toISOString(),
      });
    } else if (type === 'previous-year') {
      this.comparisonRange.set({
        start: start.subtract(1, 'year').startOf('day').utc().toISOString(),
        end: end.subtract(1, 'year').endOf('day').utc().toISOString(),
      });
    }
    console.log('[date-picker] updateComparison() resolved type =', type, 'comparisonRange =', this.comparisonRange());
  }

  private emitDateRangeChange(): void {
    const range = this.selectedRange();
    const compRange = this.comparisonRange();
    const unit = this.timeUnit();

    let previousStartDate: string | null = null;
    let previousEndDate: string | null = null;

    if (this.comparisonType() !== 'none') {
      if (compRange) {
        previousStartDate = compRange.start;
        previousEndDate = compRange.end;
      } else if (this.comparisonType() === 'previous-period') {
        const start = dayjs.utc(range.start);
        const end = dayjs.utc(range.end);
        const { start: comparisonStart, end: comparisonEnd } = getCalendarEquivalentPreviousPeriod(start, end);
        previousStartDate = comparisonStart.toISOString();
        previousEndDate = comparisonEnd.toISOString();
      } else if (this.comparisonType() === 'previous-year') {
        const start = dayjs.utc(range.start);
        const end = dayjs.utc(range.end);
        previousStartDate = start.subtract(1, 'year').toISOString();
        previousEndDate = end.subtract(1, 'year').toISOString();
      } else if (this.comparisonType() === 'previous-month') {
        const start = dayjs.utc(range.start);
        const end = dayjs.utc(range.end);
        previousStartDate = start.subtract(1, 'month').toISOString();
        previousEndDate = end.subtract(1, 'month').toISOString();
      }
    }

    const output: DateRangePickerOutput = {
      start_date: range.start,
      end_date: range.end,
      previous_start_date: previousStartDate,
      previous_end_date: previousEndDate,
      unit: unit,
      comparison_type: this.comparisonType(),
      preset_label: this.selectedType(),
    };
    console.log('[date-picker] emitDateRangeChange() output =', output);

    if (this.isMobile()) {
      this.onApply.emit(output);
    } else {
      this.dateRangeChange.emit(output);
    }
  }

  toUtc(dt: string | number, dateInstance: boolean = false) {
    const date = new Date(dt);
    if (dateInstance) {
      return new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
        date.getUTCMilliseconds()
      );
    } else {
      return Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
        date.getUTCMilliseconds()
      );
    }
  }

  private getUnitSuggestions(startDate: string, endDate: string): UnitSuggestion {
    const startTime = this.toUtc(startDate) as number;
    const endTime = this.toUtc(endDate) as number;

    if (isNaN(startTime) || isNaN(endTime)) {
      return {
        recommended: 'day',
        available: this.timeUnits.map((u: any) => u.unit),
      };
    }

    const diffMs = Math.abs(endTime - startTime);
    if (diffMs === 0) {
      return { recommended: 'hour', available: ['hour'] };
    }

    const oneDay = 86400000;
    const sevenDays = oneDay * 7;
    const oneMonth = 2629800000;
    const threeMonths = oneMonth * 3;
    const sixMonths = oneMonth * 6;
    const oneYear = oneDay * 365;

    let recommended = 'day';
    let available: string[] = ['day'];

    if (diffMs <= oneDay) {
      recommended = 'hour';
      available = ['hour'];
    } else if (diffMs <= sevenDays) {
      recommended = 'day';
      available = ['day', 'hour'];
    } else if (diffMs <= oneMonth) {
      recommended = 'week';
      available = ['day', 'week'];
    } else if (diffMs <= threeMonths) {
      recommended = 'month';
      available = ['month', 'week'];
    } else if (diffMs <= sixMonths) {
      recommended = 'month';
      available = ['month', 'week'];
    } else if (diffMs <= oneYear) {
      recommended = 'month';
      available = ['month', 'quarter'];
    } else {
      recommended = 'year';
      available = ['month', 'year'];
    }

    return { recommended, available };
  }

  public selectedUnitName = 'Day';
  public selectedUnitIndex = 1;

  private emitValue(selectPrevious?: number, overwriteUnit?: string): void {
    const range = this.selectedRange();
    const comparison = this.comparisonRange();

    const value: DateRangePickerValue = {
      startDate: range.start,
      endDate: range.end,
      unit: this.timeUnit(),
      comparisonType: this.comparisonType(),
      comparisonStartDate: comparison?.start,
      comparisonEndDate: comparison?.end,
    };

    const suggestions = this.getUnitSuggestions(value.startDate, value.endDate);
    const calculatedUnit = suggestions.recommended;
    const unitIndex = this.timeUnits.findIndex((u) => u.value === calculatedUnit);
    if (unitIndex !== -1) {
      this.selectedUnitName = this.timeUnits[unitIndex].label;
      this.selectedUnitIndex = unitIndex;
    }

    const allowedUnits = suggestions.available;
    const finalIndexes: number[] = [];
    let effectiveAllowed = allowedUnits;
    const currentAllowed = this.allowedGroupByUnitsSig();
    if (currentAllowed && currentAllowed.length > 0) {
      const allowedSet = new Set(currentAllowed);
      effectiveAllowed = allowedUnits.filter((u) => allowedSet.has(u as TimeUnit));
    }
    if (this.alwaysAvailableUnit) {
      if (Array.isArray(this.alwaysAvailableUnit)) {
        this.alwaysAvailableUnit.forEach((u) => {
          if (!effectiveAllowed.includes(u)) {
            effectiveAllowed = [...effectiveAllowed, u];
          }
        });
      } else {
        if (!effectiveAllowed.includes(this.alwaysAvailableUnit as TimeUnit)) {
          effectiveAllowed = [...effectiveAllowed, this.alwaysAvailableUnit as TimeUnit];
        }
      }
    }
    this.timeUnits.forEach((unit, index) => {
      if (effectiveAllowed.includes(unit.value)) {
        finalIndexes.push(index);
      }
    });
    this.availableUnitIndexes = finalIndexes;

    this.valueChange.emit(value);
  }

  formatDate(date: dayjs.Dayjs): string {
    return date.format('MMM D, YYYY');
  }

  public configureGroupByUnits(units: TimeUnit[] | null, alwaysAvailable: TimeUnit | null = null): void {
    this.allowedGroupByUnits = units;
    this.allowedGroupByUnitsSig.set(units);
    this.alwaysAvailableUnit = alwaysAvailable;
  }

  private filterUnits(base: Array<{ label: string; value: TimeUnit; icon: IconName }>): Array<{ label: string; value: TimeUnit; icon: IconName }> {
    let filtered = base;
    const currentAllowed = this.allowedGroupByUnitsSig();
    if (currentAllowed && currentAllowed.length > 0) {
      const allowedSet = new Set(currentAllowed);
      filtered = base.filter((u) => allowedSet.has(u.value));
    }
    if (this.alwaysAvailableUnit) {
      const exists = filtered.some((u) => u.value === this.alwaysAvailableUnit);
      if (!exists) {
        const found = base.find((u) => u.value === this.alwaysAvailableUnit);
        if (found) {
          filtered = [...filtered, found];
        } else {
          const unitMap: Record<TimeUnit, { label: string; icon: IconName }> = {
            hour: { label: 'Hour', icon: 'calendar' },
            day: { label: 'Day', icon: 'calendar' },
            week: { label: 'Week', icon: 'calendar' },
            month: { label: 'Month', icon: 'calendar' },
            quarter: { label: 'Quarter', icon: 'chartNoAxesCombined' },
            year: { label: 'Year', icon: 'chartNoAxesCombined' },
          };
          if (Array.isArray(this.alwaysAvailableUnit)) {
            this.alwaysAvailableUnit.forEach((u) => {
              const meta = unitMap[u];
              filtered = [...filtered, { label: meta.label, value: u, icon: meta.icon }];
            });
          } else {
            const meta = unitMap[this.alwaysAvailableUnit];
            filtered = [...filtered, { label: meta.label, value: this.alwaysAvailableUnit, icon: meta.icon }];
          }
        }
      }
    }
    const seen = new Set<string>();
    filtered = filtered.filter((u) => {
      if (seen.has(u.value)) return false;
      seen.add(u.value);
      return true;
    });
    return filtered;
  }

  private clampToToday(parsed: dayjs.Dayjs): dayjs.Dayjs {
    const todayLA = nowInLA().startOf('day');
    return parsed.isAfter(todayLA, 'day') ? todayLA : parsed;
  }

  formatInputDate(date: dayjs.Dayjs): string {
    return date.tz(LA_TIMEZONE).format('MM-DD-YYYY');
  }

  public autoFormatDate(event: Event, type: 'start' | 'end'): void {
    const inputElement = event.target as HTMLInputElement;
    if (!inputElement) return;

    let input = inputElement.value.replace(/\D/g, '');
    if (input.length > 8) input = input.slice(0, 8);
    if (input.length >= 4) {
      input = `${input.slice(0, 2)}-${input.slice(2, 4)}-${input.slice(4)}`;
    } else if (input.length >= 2) {
      input = `${input.slice(0, 2)}-${input.slice(2)}`;
    }

    inputElement.value = input;
    const invalidSignal = type === 'start' ? this.isDateInvalid : this.isEndDateInvalid;

    if (input.length === 10) {
      const parsed = dayjs.tz(input, 'MM-DD-YYYY', LA_TIMEZONE);
      if (parsed.isValid()) {
        invalidSignal.set(false);
        const todayLA = nowInLA().startOf('day');
        const normalizedDate = parsed.startOf('day');
        if (normalizedDate.isAfter(todayLA, 'day')) {
          inputElement.value = this.formatInputDate(todayLA);
        }
      } else {
        invalidSignal.set(true);
      }
    } else {
      invalidSignal.set(false);
    }
  }

  onStartDateChange(value: string): void {
    const parsed = dayjs.tz(value, 'MM-DD-YYYY', LA_TIMEZONE);
    if (!parsed.isValid()) {
      this.isDateInvalid.set(true);
      return;
    }
    this.isDateInvalid.set(false);

    const todayLA = nowInLA().startOf('day');
    const normalizedDate = parsed.startOf('day');
    const yesterdayLA = todayLA.subtract(1, 'day');

    // On iOS the (blur) handler can fire when the user just taps elsewhere
    // (e.g. the "Choose Period" dropdown) without actually editing the date.
    // If the parsed value matches the current Start Date, treat it as a
    // no-op so we don't overwrite the active preset label with "Custom".
    const currentRange = this.temporaryRange() || this.selectedRange();
    const currentStartNormalized = dayjs
      .utc(currentRange.start)
      .tz(LA_TIMEZONE)
      .startOf('day');
    if (normalizedDate.isSame(currentStartNormalized, 'day')) {
      return;
    }

    let newStart: dayjs.Dayjs;
    let newEnd: dayjs.Dayjs;

    if (normalizedDate.isSame(todayLA, 'day')) {
      newStart = yesterdayLA;
      newEnd = todayLA;
    } else {
      const currentEnd = dayjs.utc(currentRange.end).tz(LA_TIMEZONE).startOf('day');
      const effectiveEnd = currentEnd.isBefore(normalizedDate, 'day') ? normalizedDate : currentEnd;
      newStart = normalizedDate;
      newEnd = effectiveEnd;
    }

    const newRange: DateRange = {
      start: newStart.utc().toISOString(),
      end: newEnd.utc().toISOString(),
    };

    this.temporaryRange.set(newRange);
    // this.selectedType.set('Custom');
    // this.selectedPreset.set('custom');
    this.setStartCalendarMonth(newStart);
  }

  onEndDateChange(value: string): void {
    const parsed = dayjs.tz(value, 'MM-DD-YYYY', LA_TIMEZONE);
    if (!parsed.isValid()) {
      this.isEndDateInvalid.set(true);
      return;
    }
    this.isEndDateInvalid.set(false);

    const todayLA = nowInLA().startOf('day');
    const normalizedDate = parsed.startOf('day');
    const yesterdayLA = todayLA.subtract(1, 'day');
    const currentRange = this.temporaryRange() || this.selectedRange();
    const currentStart = dayjs.utc(currentRange.start).tz(LA_TIMEZONE).startOf('day');

    // On iOS the (blur) handler can fire when the user just taps elsewhere
    // (e.g. the "Choose Period" dropdown) without actually editing the date.
    // If the parsed value matches the current End Date, treat it as a
    // no-op so we don't overwrite the active preset label with "Custom".
    const currentEndNormalized = dayjs
      .utc(currentRange.end)
      .tz(LA_TIMEZONE)
      .startOf('day');
    if (normalizedDate.isSame(currentEndNormalized, 'day')) {
      return;
    }

    let newStart: dayjs.Dayjs;
    let newEnd: dayjs.Dayjs;

    const dayDiff = normalizedDate.diff(currentStart, 'day');

    if (normalizedDate.isSame(todayLA, 'day') && dayDiff <= 1) {
      newStart = yesterdayLA;
      newEnd = todayLA;
    } else {
      newStart = currentStart;
      newEnd = normalizedDate;
    }

    const newRange: DateRange = {
      start: newStart.utc().toISOString(),
      end: newEnd.utc().toISOString(),
    };

    this.temporaryRange.set(newRange);
    this.selectedType.set('Custom');
    this.selectedPreset.set('custom');
  }

  onComparisonStartDateChange(value: string): void {
    try {
      const parsed = dayjs.tz(value, 'MM-DD-YYYY', LA_TIMEZONE);
      if (!parsed.isValid()) return;

      const todayLA = nowInLA().startOf('day');
      const normalizedDate = parsed.startOf('day');

      if (normalizedDate.isAfter(todayLA, 'day')) return;

      const existingEnd = this.comparisonRange()
        ? dayjs.utc(this.comparisonRange()!.end).tz(LA_TIMEZONE).startOf('day')
        : normalizedDate;

      const effectiveEnd = existingEnd.isBefore(normalizedDate, 'day') ? normalizedDate : existingEnd;

      this.setComparisonRange({
        start: normalizedDate.utc().toISOString(),
        end: effectiveEnd.utc().toISOString(),
      });
    } catch (error) {
      console.error(error);
    }
  }

  onComparisonEndDateChange(value: string): void {
    try {
      const parsed = dayjs.tz(value, 'MM-DD-YYYY', LA_TIMEZONE);
      if (!parsed.isValid()) return;

      const todayLA = nowInLA().startOf('day');
      const normalizedDate = parsed.startOf('day');

      if (normalizedDate.isAfter(todayLA, 'day')) return;

      const existingStart = this.comparisonRange()
        ? dayjs.utc(this.comparisonRange()!.start).tz(LA_TIMEZONE).startOf('day')
        : normalizedDate;

      const effectiveStart = existingStart.isAfter(normalizedDate, 'day') ? normalizedDate : existingStart;

      this.setComparisonRange({
        start: effectiveStart.utc().toISOString(),
        end: normalizedDate.utc().toISOString(),
      });
    } catch (error) {
      console.error(error);
    }
  }

  formatDateInLA(isoString: string): string {
    return dayjs.utc(isoString).tz(LA_TIMEZONE).format('MMM D, YYYY');
  }

  getDaysDifference(start: dayjs.Dayjs, end: dayjs.Dayjs): number {
    return getInclusiveDayCount(start, end);
  }

  trackByDate(index: number, day: CalendarDay): string {
    return day.date.format('YYYY-MM-DD');
  }

  getYearOptions(): number[] {
    const currentYear = nowInLA().year();
    return Array.from({ length: 9 }, (_, i) => currentYear - i);
  }

  getDayClasses(day: CalendarDay): string {
    const classes = [];
    if (day.isDisabled || !day.isCurrentMonth) {
      classes.push('opacity-25 cursor-not-allowed');
      if (!day.isCurrentMonth) {
        classes.push('text-gray-400 dark:text-gray-600');
        return classes.join(' ');
      }
    } else {
      classes.push('cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600/50');
    }

    if (day.isCurrentMonth) {
      class90: classes.push('text-gray-900 dark:text-white');
      if (day.isToday && !day.isSelected) {
        classes.push('text-purple-700 dark:text-purple-300 font-medium');
      }

      if (day.isStartDate || day.isEndDate) {
        classes.push('bg-gradient-to-br from-purple-600 to-indigo-600 text-white shadow-lg scale-110 z-10');
      } else if (day.isInRange) {
        classes.push('bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-200');
      }

      if (day.isComparisonStart || day.isComparisonEnd) {
        classes.push('bg-gradient-to-br from-purple-600 to-indigo-600 text-white font-medium');
      } else if (day.isComparisonRange) {
        classes.push('bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-200');
      }
    }
    return classes.join(' ');
  }

  readonly MIN_DATE = dayjs.utc('2017-01-01');
  get MAX_DATE() {
    return nowInLA();
  }

  isStartPrevDisabled(): boolean {
    return this.startCalendarMonth().isSameOrBefore(this.MIN_DATE, 'month');
  }

  isStartNextDisabled(): boolean {
    const nextStart = this.startCalendarMonth().add(1, 'month');
    const todayLA = nowInLA().startOf('month');
    return (
      nextStart.isAfter(todayLA, 'month') ||
      nextStart.isAfter(this.endCalendarMonth(), 'month')
    );
  }

  isEndPrevDisabled(): boolean {
    const prevEnd = this.endCalendarMonth().subtract(1, 'month');
    return (
      this.endCalendarMonth().isSameOrBefore(this.startCalendarMonth(), 'month') ||
      prevEnd.isBefore(this.MIN_DATE, 'month')
    );
  }

  isEndNextDisabled(): boolean {
    return this.endCalendarMonth().isSameOrAfter(this.MAX_DATE, 'month');
  }

  isOpen = false;
  isYearOpen = false;
  isNextMonthOpen = false;
  isNextYearOpen = false;

  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  setMonthAndClose(monthIndex: number) {
    if (!this.isStartMonthValid(monthIndex)) return;

    this.setCalendarMonth(monthIndex, this.startCalendarMonth().year());
    this.isOpen = false;
  }

  setYearAndClose(year: number) {
    if (!this.isStartYearValid(year)) return;

    this.setCalendarMonth(this.startCalendarMonth().month(), year);
    this.isYearOpen = false;
  }

  endMonthOpen = false;
  endYearOpen = false;

  setEndMonthAndClose(monthIndex: number) {
    if (!this.isEndMonthValid(monthIndex)) return;

    const newDate = nowInLA()
      .year(this.endCalendarMonth().year())
      .month(monthIndex)
      .startOf('month');
    this.endCalendarMonth.set(newDate);
    if (newDate.isBefore(this.startCalendarMonth(), 'month')) {
      this.startCalendarMonth.set(newDate);
    }
    this.isEndMonthOpen.set(false);
  }

  setEndYearAndClose(year: number) {
    if (!this.isEndYearValid(year)) return;

    const newDate = nowInLA()
      .year(year)
      .month(this.endCalendarMonth().month())
      .startOf('month');
    this.endCalendarMonth.set(newDate);
    if (newDate.isBefore(this.startCalendarMonth(), 'month')) {
      this.startCalendarMonth.set(newDate);
    }
    this.isEndYearOpen.set(false);
  }

  endYear = this.endCalendarMonth().year();
  endMonth = this.endCalendarMonth().month();

  isEndMonthValid(monthIndex: number): boolean {
    return this.isEndMonthValidForYear(monthIndex, this.endCalendarMonth().year());
  }

  isEndYearValid(year: number): boolean {
    return this.isEndMonthValidForYear(this.endCalendarMonth().month(), year);
  }

  private isEndMonthValidForYear(monthIndex: number, year: number): boolean {
    const todayLA = nowInLA();
    const proposedEndCalendar = nowInLA().year(year).month(monthIndex).startOf('month');

    if (proposedEndCalendar.isAfter(todayLA, 'month')) return false;
    if (proposedEndCalendar.isBefore(this.MIN_DATE, 'month')) return false;
    return true;
  }

  selectMobileMonth(index: number) {
    const newMonth = this.startCalendarMonth().month(index).startOf('month');
    this.startCalendarMonth.set(newMonth);
    this.endCalendarMonth.set(newMonth);
    // Drop the user back into the date grid so they can pick the actual day.
    this.mobileView.set('date');
  }

  selectMobileYear(year: number) {
    const newYear = this.startCalendarMonth().year(year).startOf('month');
    this.startCalendarMonth.set(newYear);
    this.endCalendarMonth.set(newYear);
    // After picking the year, drill down to the month tab next.
    this.mobileView.set('month');
  }

  clearMobile() {
    const defaultRange = this.predefinedRanges.find((r) => r.label === 'Last 7 Days') ?? this.predefinedRanges[0];

    this.timeUnit.set(this.defaultTimeUnit);
    this.comparisonType.set('none');
    this.comparisonRange.set(null);
    this.isMobilePresetOpen.set(false);
    this.isMobileGroupByOpen.set(false);

    if (defaultRange) {
      this.applyMobilePreset(defaultRange);
    } else {
      this.selectedPreset.set('last7days');
    }
  }


  onEndYearChange(year: number) {
    this.setCalendarMonth(this.endMonth, year, true);
  }

  onEndMonthChange(month: number) {
    this.setCalendarMonth(month, this.endYear, true);
  }

  onMobilePresetClick(preset: { label: string, preset: string }) {
    this.isMobilePresetOpen.set(false);

    if (preset.preset === 'custom') {
      this.selectedPreset.set('custom');
      this.selectedType.set('Custom');
      this.isSelectingStart.set(true);
      return;
    }

    const range = this.predefinedRanges.find(
      (r) => this.getPresetId(r.label) === preset.preset.toLowerCase()
    );

    if (range) {
      this.applyMobilePreset(range);
      this.applyRange();
    } else {
      this.selectedPreset.set(preset.preset.toLowerCase());
      this.selectedType.set(preset.label);
      if (preset.preset === 'today') {
        const today = nowInLA().startOf('day');
        this.selectedRange.set({ start: today.format(), end: today.endOf('day').format() });
      } else if (preset.preset === 'thisWeek') {
        const now = nowInLA();
        this.selectedRange.set({ start: now.startOf('isoWeek').format(), end: now.endOf('day').format() });
      }
      this.temporaryRange.set(null);
      this.originalRange.set(null);
      this.applyRange();
    }
  }


  private applyMobilePreset(preset: PredefinedRange): void {
    this.selectedType.set(preset.label);
    const range = preset.getValue();
    this.selectedRange.set(range);
    this.temporaryRange.set(null);
    this.originalRange.set(null);
    this.selectedPreset.set(this.getPresetId(preset.label));
    this.updateCalendarMonths(
      dayjs.tz(range.start, LA_TIMEZONE),
      dayjs.tz(range.end, LA_TIMEZONE)
    );
    this.autoSelectTimeUnit();
    this.updateComparison();
    this.isSelectingStart.set(true);
  }

  toggleMobilePreset() {
    this.isMobilePresetOpen.update(v => !v);
  }

  setMobileView(view: 'year' | 'month' | 'date') {
    this.mobileView.set(view);
  }

  getPresetId(label: string): string {
    return label.toLowerCase().replace(/\s+/g, '');
  }
}